import React, { useState, useEffect } from 'react';
import { ProductVariation } from '../../types/product';

interface VariationSelectorProps {
  variations: ProductVariation[];
  variationAttributes: Record<string, string[]>;
  onVariationSelect: (variation: ProductVariation) => void;
  selectedVariation?: ProductVariation;
  disabled?: boolean;
}

export const VariationSelector: React.FC<VariationSelectorProps> = ({
  variations,
  variationAttributes,
  onVariationSelect,
  selectedVariation,
  disabled = false
}) => {
  const [selectedAttributes, setSelectedAttributes] = useState<Record<string, string>>({});

  // Update selected attributes when selectedVariation changes
  useEffect(() => {
    if (selectedVariation && selectedVariation.attributes) {
      setSelectedAttributes(selectedVariation.attributes);
    }
  }, [selectedVariation]);

  // Handle attribute selection
  const handleAttributeChange = (attributeName: string, value: string) => {
    const newAttributes = { ...selectedAttributes, [attributeName]: value };
    setSelectedAttributes(newAttributes);

    // Find matching variation
    const matchingVariation = variations.find(variation => {
      return Object.keys(newAttributes).every(attrName => 
        variation.attributes[attrName] === newAttributes[attrName]
      );
    });

    if (matchingVariation) {
      onVariationSelect(matchingVariation);
    }
  };

  // Get available options for an attribute based on current selections
  const getAvailableOptions = (attributeName: string): string[] => {
    const otherAttributes = { ...selectedAttributes };
    delete otherAttributes[attributeName];

    return variations
      .filter(variation => {
        return Object.keys(otherAttributes).every(attrName => 
          variation.attributes[attrName] === otherAttributes[attrName]
        );
      })
      .map(variation => variation.attributes[attributeName])
      .filter((value, index, array) => array.indexOf(value) === index);
  };

  // Check if an option is available
  const isOptionAvailable = (attributeName: string, value: string): boolean => {
    return getAvailableOptions(attributeName).includes(value);
  };

  // Get stock status for an option - consider other selected attributes
  const getOptionStockStatus = (attributeName: string, value: string): 'instock' | 'outofstock' | 'onbackorder' => {
    const otherAttributes = { ...selectedAttributes };
    delete otherAttributes[attributeName];
    const matching = variations.filter(v => {
      if (v.attributes[attributeName] !== value) return false;
      return Object.keys(otherAttributes).every(a => v.attributes[a] === otherAttributes[a]);
    });
    if (matching.length === 0) return 'outofstock';
    // If any matching variation is in stock, treat option as in stock
    const anyInStock = matching.some(v => {
      const status = String(v.stockStatus || '').toLowerCase();
      if (status === 'outofstock') return false;
      if (v.stockQuantity === undefined || v.stockQuantity === null) return true;
      return Number(v.stockQuantity) > 0;
    });
    return anyInStock ? 'instock' : 'outofstock';
  };

  if (!variations || variations.length === 0) {
    return (
      <div className="variation-selector">
        <div className="selected-variation-info" style={{ color: '#f87171' }}>
          Failed to load options
        </div>
      </div>
    );
  }

  if (!variationAttributes || Object.keys(variationAttributes).length === 0) {
    return (
      <div className="variation-selector">
        <div className="selected-variation-info" style={{ color: '#f87171' }}>
          Failed to load options
        </div>
      </div>
    );
  }
  const singleAttribute = Object.keys(variationAttributes).length === 1;

  if (singleAttribute) {
    const attrName = Object.keys(variationAttributes)[0];
    return (
      <div className="variation-selector">
        <div className="variation-attribute" style={{ marginBottom: 12 }}>
          <label className="variation-label" style={{ display: 'block', marginBottom: 8, fontWeight: 600 }}>
            Select Variation <span style={{ color: '#ffffff', fontWeight: 400 }}>(Selected: {(() => {
              if (!selectedVariation) return 'None';
              const a = selectedVariation.attributes || {} as any;
              return a[attrName] || a[`attribute_${attrName}`] || a[`pa_${attrName}`] || a[`attribute_pa_${attrName}`] || Object.values(a)[0] || 'None';
            })()})</span>
          </label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, maxWidth: '400px' }}>
            {variations.map((v) => {
              const isSel = selectedVariation?.id === v.id;
              const status = String(v.stockStatus || '').toLowerCase();
              const inStock = status !== 'outofstock' && (v.stockQuantity == null || Number(v.stockQuantity) > 0);
              const attrs: any = v.attributes || {};
              const optionLabel = attrs[attrName] || attrs[`attribute_${attrName}`] || attrs[`pa_${attrName}`] || attrs[`attribute_pa_${attrName}`] || Object.values(attrs)[0] || 'Option';
              return (
                <button
                  key={v.id}
                  type="button"
                  onClick={() => { if (inStock) { onVariationSelect(v); setSelectedAttributes(v.attributes); } }}
                  disabled={disabled || !inStock}
                  style={{
                    textAlign: 'left',
                    width: '100%',
                    maxWidth: '400px',
                    padding: '14px 16px',
                    borderRadius: 4,
                    border: isSel ? '2px solid var(--orange)' : '1px solid rgba(255,255,255,0.15)',
                    background: 'rgba(0,0,0,0.25)',
                    color: 'var(--white)',
                    opacity: inStock ? 1 : 0.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 12,
                  }}
                >
                  <div>
                    <div style={{ fontWeight: 600 }}>{optionLabel}</div>
                    <div style={{ fontSize: 12, opacity: 0.8 }}>{inStock ? (v.stockQuantity != null ? `${v.stockQuantity} in stock` : 'In stock') : 'Out of stock'}</div>
                  </div>
                  <div style={{ textAlign: 'right', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
                    {isSel && (
                      <span style={{
                        fontSize: 12,
                        color: '#ffffff',
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                      }}>
                        Selected
                      </span>
                    )}
                    {v.onSale && v.regularPrice ? (
                      <>
                        <div style={{ fontWeight: 700, color: 'var(--orange)' }}>R {Number(v.price).toLocaleString()}</div>
                        <div style={{ fontSize: 12, textDecoration: 'line-through', opacity: 0.7 }}>R {Number(v.regularPrice).toLocaleString()}</div>
                      </>
                    ) : (
                      <div style={{ fontWeight: 700, color: 'var(--orange)' }}>R {Number(v.price).toLocaleString()}</div>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="variation-selector">
      {Object.entries(variationAttributes).map(([attributeName, options]) => (
        <div key={attributeName} className="variation-attribute">
          <label className="variation-label">
            {attributeName}:
          </label>
          <div className="variation-options">
            {options.map(option => {
              const isAvailable = isOptionAvailable(attributeName, option);
              const stockStatus = getOptionStockStatus(attributeName, option);
              const isSelected = selectedAttributes[attributeName] === option;
              const isOutOfStock = stockStatus === 'outofstock';

              return (
                <button
                  key={option}
                  type="button"
                  className={`variation-option ${isSelected ? 'selected' : ''} ${isOutOfStock ? 'out-of-stock' : ''}`}
                  onClick={() => handleAttributeChange(attributeName, option)}
                  disabled={disabled || !isAvailable || isOutOfStock}
                  title={isOutOfStock ? 'Out of stock' : isAvailable ? `Select ${option}` : 'Not available'}
                >
                  {option}
                  {isOutOfStock && <span className="stock-indicator">(Out of Stock)</span>}
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};
